import { createContext, use } from 'react';
import type { SessionInfo, UserRole } from '@bartizo/shared';

/**
 * The session context and the hooks that read it, kept apart from the provider
 * that fills it.
 *
 * The split is for fast refresh, which only works when a module exports components
 * or values but not both — and it reads better anyway: this file is the contract,
 * `session.tsx` is the implementation.
 */

export const SESSION_QUERY_KEY = ['session'] as const;

export type SessionStatus = 'loading' | 'signed-out' | 'signed-in';

export interface SessionContextValue {
  status: SessionStatus;
  /** Present exactly when `status` is `signed-in`. */
  session: SessionInfo | null;
  /** `totpCode` only after the API has answered `totp_required`. */
  signIn: (email: string, password: string, totpCode?: string) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Replaces the cached session with one the API just returned — what the routes
   * that change an account answer with, so nothing needs a refetch afterwards.
   */
  applySession: (session: SessionInfo) => void;
  /** Records a successful re-check so the gate reopens without a refetch. */
  noteReauthenticated: (at: string) => void;
  /** Section 20's display time zone; `null` restores the installation default. */
  setTimezone: (timezone: string | null) => Promise<void>;
  /** Whether a high-risk action can go ahead without asking for the password. */
  hasFreshReauth: () => boolean;
  /**
   * A failure that is *not* "you are signed out" — typically the API being
   * unreachable. The two need different answers from whoever is looking at the
   * screen, so they are not collapsed into one signed-out state.
   */
  loadError: Error | null;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const context = use(SessionContext);
  if (context === null) throw new Error('useSession must be used inside a SessionProvider');
  return context;
}

/** The signed-in user, for the many places that only make sense when there is one. */
export function useCurrentUser(): SessionInfo {
  const { session } = useSession();
  if (session === null) throw new Error('useCurrentUser used outside a signed-in tree');
  return session;
}

export function useRole(): UserRole {
  return useCurrentUser().user.role;
}
